import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Space,
  Descriptions,
  Empty,
  Spin,
  Alert,
} from 'antd';
import MobileTable from '../../Component/MobileTable';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import Api from '../../Network';
import { usePermissions } from '../../contexts/PermissionContext';
import './InvoiceDetail.scss';

const { Title, Text } = Typography;

type InvoiceDetailProps = Record<string, never>;

const InvoiceDetail: React.FC<InvoiceDetailProps> = () => {
  const { t } = useTranslation();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [invoice, setInvoice] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch invoice details
  const fetchInvoiceDetails = async (invoiceId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await Api.get(`/api/admin/invoices/${invoiceId}`);

      if (response && response.data) {
        setInvoice(response.data);
      } else if (response && response.status === false) {
        setError(response.message || t('invoices.messages.noInvoiceFound', 'Invoice not found'));
      } else if (response && typeof response === 'object') {
        // If response is directly the data object
        setInvoice(response);
      } else {
        setError(t('invoices.messages.noInvoiceFound', 'Invoice not found'));
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setError(t('invoices.messages.operationFailed', 'Failed to load invoice details'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchInvoiceDetails(id);
    }
  }, [id]);

  const handleBack = () => {
    history.goBack();
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      paid: 'green',
      unpaid: 'orange',
      overdue: 'red',
      partial: 'blue',
      cancelled: 'red',
    };
    return statusColors[status.toLowerCase()] || 'default';
  };

  if (loading) {
    return (
      <div className="invoice-detail-page" style={{ padding: '24px' }}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="invoice-detail-page" style={{ padding: '24px' }}>
        <Alert
          message={t('common.error')}
          description={error || t('invoices.messages.noInvoiceFound')}
          type="error"
          showIcon
          style={{ margin: '24px' }}
        />
      </div>
    );
  }

  const invoiceItemsColumns = [
    {
      title: t('invoices.columns.product', 'Product'),
      dataIndex: ['product', 'name_ar'],
      key: 'product',
      render: (name: string, record: any) => (
        <div className="product-info">
          <div className="product-name">{name || record.product?.name_en || 'N/A'}</div>
          {record.product?.name_en && (
            <Text type="secondary" className="product-name-en">
              {record.product.name_en}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: t('invoices.columns.quantity', 'Quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (quantity: string) => (
        <Text strong className="quantity-text">
          {parseFloat(quantity || '0').toFixed(2)}
        </Text>
      ),
    },
    {
      title: t('invoices.columns.unitPrice', 'Unit Price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      render: (price: string) => (
        <Text className="price-text">
          {parseFloat(price || '0').toFixed(2)} {t('common.SAR')}
        </Text>
      ),
    },
    {
      title: t('invoices.columns.discount', 'Discount'),
      dataIndex: 'discount',
      key: 'discount',
      width: 100,
      render: (discount: string) => {
        const discountValue = parseFloat(discount || '0');
        return (
          <Text className="discount-text">
            {discountValue > 0 ? `${discountValue.toFixed(2)} ${t('common.SAR')}` : '-'}
          </Text>
        );
      },
    },
    {
      title: t('invoices.columns.subtotalBeforeTax', 'Subtotal Before Tax'),
      key: 'subtotal_before_tax',
      width: 150,
      render: (record: any) => {
        const quantity = parseFloat(record.quantity || '0');
        const unitPrice = parseFloat(record.unit_price || '0');
        const discount = parseFloat(record.discount || '0');
        const subtotal = quantity * unitPrice - discount;
        return (
          <Text className="subtotal-text">
            {subtotal.toFixed(2)} {t('common.SAR')}
          </Text>
        );
      },
    },
    {
      title: t('invoices.columns.taxPercent', 'Tax'),
      dataIndex: 'tax_percent',
      key: 'tax_percent',
      width: 80,
      render: (taxPercent: string) => (
        <Text className="tax-percent-text">{parseFloat(taxPercent || '0').toFixed(0)}%</Text>
      ),
    },
    {
      title: t('invoices.columns.taxAmount', 'Tax Amount'),
      key: 'tax_amount',
      width: 120,
      render: (record: any) => {
        const quantity = parseFloat(record.quantity || '0');
        const unitPrice = parseFloat(record.unit_price || '0');
        const discount = parseFloat(record.discount || '0');
        const taxPercent = parseFloat(record.tax_percent || '0');
        const subtotal = quantity * unitPrice - discount;
        const taxAmount = subtotal * (taxPercent / 100);
        return (
          <Text className="tax-amount-text">
            {taxAmount.toFixed(2)} {t('common.SAR')}
          </Text>
        );
      },
    },
    {
      title: t('invoices.columns.totalAfterTax', 'Total After Tax'),
      key: 'total_after_tax',
      width: 140,
      render: (record: any) => {
        const quantity = parseFloat(record.quantity || '0');
        const unitPrice = parseFloat(record.unit_price || '0');
        const discount = parseFloat(record.discount || '0');
        const taxPercent = parseFloat(record.tax_percent || '0');
        const subtotal = quantity * unitPrice - discount;
        const taxAmount = subtotal * (taxPercent / 100);
        const totalAfterTax = subtotal + taxAmount;
        return (
          <Text strong className="total-after-tax-text">
            {totalAfterTax.toFixed(2)} {t('common.SAR')}
          </Text>
        );
      },
    },
  ];

  return (
    <div className="invoice-detail-page" style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: 16 }}>
          {t('common.back')}
        </Button>

        <Title level={2} style={{ margin: 0 }}>
          {t('invoices.details.title', 'Invoice Details')} - {invoice.reference}
        </Title>
      </div>

      {/* Invoice Header */}
      <Card className="invoice-header-card" style={{ marginBottom: '24px' }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12}>
            <Descriptions column={1} size="small" className="invoice-info">
              <Descriptions.Item label={t('invoices.columns.reference', 'Reference')}>
                <Text code strong className="reference-text">
                  {invoice.reference || 'N/A'}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('invoices.columns.status', 'Status')}>
                <Tag color={getStatusColor(invoice.status)} className="status-tag">
                  {invoice.status || 'N/A'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('invoices.columns.issueDate', 'Issue Date')}>
                <div className="date-info">
                  <CalendarOutlined />{' '}
                  {invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString() : 'N/A'}
                </div>
              </Descriptions.Item>
              {invoice.due_date && (
                <Descriptions.Item label={t('invoices.columns.dueDate', 'Due Date')}>
                  <div className="date-info">
                    <CalendarOutlined /> {new Date(invoice.due_date).toLocaleDateString()}
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Col>
          <Col xs={24} sm={12}>
            <Descriptions column={1} size="small" className="invoice-info">
              <Descriptions.Item label={t('invoices.columns.customer', 'Customer')}>
                <div className="customer-info">
                  <UserOutlined /> {invoice.contact?.name || 'N/A'}
                </div>
              </Descriptions.Item>
              {invoice.contact?.phone_number && (
                <Descriptions.Item label={t('invoices.columns.phone', 'Phone')}>
                  <div className="phone-info">
                    <PhoneOutlined /> {invoice.contact.phone_number}
                  </div>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('invoices.columns.total', 'Total')}>
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  {parseFloat(invoice.total || '0').toFixed(2)} {t('common.SAR')}
                </Text>
              </Descriptions.Item>
              {invoice.paid_amount && (
                <Descriptions.Item label={t('invoices.columns.paidAmount', 'Paid Amount')}>
                  <Text strong style={{ color: '#52c41a' }}>
                    {parseFloat(invoice.paid_amount).toFixed(2)} {t('common.SAR')}
                  </Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* Invoice Items */}
      {invoice.line_items && invoice.line_items.length > 0 ? (
        <Card
          title={t('invoices.details.items', 'Invoice Items')}
          className="invoice-items-card"
          style={{ marginBottom: '24px' }}
        >
          <MobileTable
            columns={invoiceItemsColumns}
            dataSource={invoice.line_items}
            rowKey="id"
            pagination={false}
            size="small"
            className="invoice-items-table"
            scroll={{ x: 1100 }}
          />
        </Card>
      ) : (
        <Card
          title={t('invoices.details.items', 'Invoice Items')}
          className="invoice-items-card"
          style={{ marginBottom: '24px' }}
        >
          <Empty
            description={t('invoices.details.noItems', 'No invoice items found')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}

      {/* Invoice Summary */}
      <Card
        title={t('invoices.details.summary', 'Invoice Summary')}
        style={{ marginBottom: '24px' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                  {parseFloat(invoice.total || '0').toFixed(2)} {t('common.SAR')}
                </div>
                <div style={{ color: 'var(--color-text-secondary)' }}>
                  {t('invoices.details.totalAmount', 'Total Amount')}
                </div>
              </div>
            </Card>
          </Col>
          {invoice.paid_amount && (
            <Col xs={24} sm={8}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                    {parseFloat(invoice.paid_amount).toFixed(2)} {t('common.SAR')}
                  </div>
                  <div style={{ color: 'var(--color-text-secondary)' }}>
                    {t('invoices.details.paidAmount', 'Paid Amount')}
                  </div>
                </div>
              </Card>
            </Col>
          )}
          {invoice.due_amount && (
            <Col xs={24} sm={8}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                    {parseFloat(invoice.due_amount).toFixed(2)} {t('common.SAR')}
                  </div>
                  <div style={{ color: 'var(--color-text-secondary)' }}>
                    {t('invoices.details.dueAmount', 'Due Amount')}
                  </div>
                </div>
              </Card>
            </Col>
          )}
        </Row>
      </Card>

      {/* Actions */}
      {(hasPermission('download_invoice') ||
        hasPermission('invoices.download') ||
        hasPermission('export_invoices') ||
        hasAnyPermission(['view_invoices', 'view_invoice', 'invoices.read', 'invoices.view'])) && (
        <Card title={t('invoices.details.actions', 'Actions')} style={{ marginBottom: '24px' }}>
          <Space>
            {(hasPermission('download_invoice') ||
              hasPermission('invoices.download') ||
              hasPermission('export_invoices')) && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() =>
                  window.open(
                    `https://driveshield.net/storage/invoices/pdf/${invoice.id}/invoice.pdf`,
                    '_blank'
                  )
                }
              >
                {t('invoices.details.download', 'Download Invoice')}
              </Button>
            )}
            {(hasPermission('view_invoices') ||
              hasPermission('view_invoice') ||
              hasPermission('invoices.read') ||
              hasPermission('invoices.view')) && (
              <Button
                icon={<FileTextOutlined />}
                onClick={() =>
                  window.open(
                    `https://driveshield.net/storage/invoices/pdf/${invoice.id}/invoice.pdf`,
                    '_blank'
                  )
                }
              >
                {t('invoices.details.preview', 'Preview Invoice')}
              </Button>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default InvoiceDetail;
